import React, { useCallback, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Tappable } from '@vectord/components';
import classNames from 'classnames';

import {
  blacklistedMenteeDetailSelector,
  blacklistedMenteeInputErrorSelector,
  BLACKLIST_MANAGE_MODAL_STAGES,
  setBlacklistedMenteeManageLearner,
  setInputErrors,
  setPersonalisedMenteeEmailersManageModalStage,
  validateBlacklistManageModalErrors,
  validateBlacklistPauseDates,
} from 'features/communicationManagement';
import Field from 'components/Field';
import PersonalisedMenteeEmailersTimeInput from '../PersonalisedMenteeEmailersTimeInput';
import SectionHeading from 'components/SectionHeading';
import snoozingBell from 'assets/icons/snoozingBell.svg';
import styles from './PersonalisedMenteeEmailersManageLearnerModal.module.scss';

export default function PersonalisedMenteeEmailersManageLearnerModal() {
  const dispatch = useDispatch();
  const inputErrors = useSelector(blacklistedMenteeInputErrorSelector);
  const learnerDetail = useSelector(blacklistedMenteeDetailSelector);
  const [startDate, setStartDate] = useState(learnerDetail.pause_from);
  const [endDate, setEndDate] = useState(learnerDetail.resume_from);

  const handleStartDate = useCallback((date) => {
    setStartDate(date);
  }, []);

  const handleEndDate = useCallback((date) => {
    setEndDate(date);
  }, []);

  const shouldValidateManageModalErrors = useCallback(() => {
    const manageModalErrors = validateBlacklistManageModalErrors(
      startDate,
      endDate
    );
    if (Object.keys(manageModalErrors).length !== 0) {
      dispatch(setInputErrors(manageModalErrors));
      return true;
    } else {
      dispatch(setInputErrors(null));
      return false;
    }
  }, [dispatch, endDate, startDate]);

  const shouldValidateDateInput = useCallback(() => {
    const dateInputErrors = validateBlacklistPauseDates(startDate, endDate);
    if (Object.keys(dateInputErrors).length !== 0) {
      dispatch(setInputErrors(dateInputErrors));
      return true;
    } else {
      dispatch(setInputErrors(null));
      return false;
    }
  }, [dispatch, endDate, startDate]);

  const handleUpdateClick = useCallback(() => {
    if (shouldValidateManageModalErrors() || shouldValidateDateInput()) return;

    const updateData = {
      ...learnerDetail,
      start_date: startDate,
      end_date: endDate,
    };

    batch(() => {
      dispatch(setBlacklistedMenteeManageLearner(updateData));
      dispatch(
        setPersonalisedMenteeEmailersManageModalStage(
          BLACKLIST_MANAGE_MODAL_STAGES.manageConfirmation
        )
      );
    });
  }, [
    dispatch,
    endDate,
    startDate,
    shouldValidateDateInput,
    shouldValidateManageModalErrors,
    learnerDetail,
  ]);

  return (
    <>
      <SectionHeading
        title={
          <div className={styles.header}>
            <img
              src={snoozingBell}
              className="primary m-r-xs h2"
              alt="icon"
            />
            MANAGE:
          </div>
        }
      />
      <Field className={classNames(styles.inputField, 'm-b-xs')}>
        <div className={classNames(styles.learnerEmail, 'm-r-xxs')}>
          Learner Email:
        </div>
        {learnerDetail.user_email}
      </Field>
      <PersonalisedMenteeEmailersTimeInput
        pauseStartDate={startDate}
        handlePauseStartDateChange={handleStartDate}
        pauseEndDate={endDate}
        handlePauseEndDateChange={handleEndDate}
        inputErrors={inputErrors}
      />
      <Tappable
        className={classNames(styles.action, 'p-v-xxs', 'm-t-m')}
        onClick={handleUpdateClick}
      >
        Update
      </Tappable>
    </>
  );
}
